import 'deep-chat';
import '@atlas/shared/client/ui/wc/components/button';
import '@atlas/shared/client/ui/wc/components/loading-overlay';
import '@atlas/shared/client/ui/wc/icons/IconHelp';
import '@atlas/shared/client/ui/wc/icons/brands/IconWhatsapp';
import '@atlas/shared/client/ui/wc/icons/IconChat';
import '@atlas/shared/client/ui/wc/icons/IconClose';
import '@atlas/shared/client/ui/wc/icons/IconLess';
import '@atlas/shared/client/ui/wc/icons/IconSmartphone';
import { html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { ref } from 'lit/directives/ref.js';
import { Task, TaskStatus } from '@lit/task';
import { DeepChatStyle, chatbotStyle } from './Chatbot.styles';
import type { DeepChat } from 'deep-chat';
import { t } from 'i18next';
import { ChatbotService, type SendQuestionRequestModel } from '@atlas/shared/data-access/chatbot';
import { SuperElement } from '@atlas/shared/client/ui/wc/internals/super/SuperElement';
import { animate, fadeIn, fadeOut } from '@lit-labs/motion';
import { ChatbotTranslationsLoader } from '../../services/ChatbotTranslationsLoader';
import { BroadcastController } from '../../controllers/BroadcastController';
import type { MessageContent } from 'deep-chat/dist/types/messages';
import { styleObjectToString, sleep, isMobileDevice, isAppDevice } from '@atlas/shared/client/ui/core/utils';
import { createChatbotMessageHtml } from '../chatbot-message/ChatbotMessage';
import { sanitizeUserInput } from '../../utils/sanitize';
import { SizeLrBreakpointMd } from '@laredoute/design-system/tokens';
import './ChatbotMinimized';

declare const __PKG_VERSION__: string;

export const LR_CHATBOT_BROADCAST_CHANNEL_ID = 'lr.chatbot.broadcast_channel.' + document.location.hostname;
export const LR_CHATBOT_LOCALSTORAGE_CONVERSATION_ID = 'lr.chatbot.conversation_id';
const LR_CHATBOT_LOCALSTORAGE_CONVERSATION_STATE = 'lr.chatbot.conversation_state';

type ChatbotDisplayState = 'opened' | 'minimized';

export type ChatbotBroadcastMessage =
    | { action: 'open' }
    | { action: 'close' }
    | { action: 'addMessage'; payload: { message: MessageContent } }
    | { action: 'end-conversation'; payload: { conversationId: string } };

@customElement('lr-chatbot')
export class Chatbot extends SuperElement {
    static styles = [SuperElement.styles, chatbotStyle];

    @property({ type: String })
    accessor apiUrl = '/bff';

    @property({ type: Boolean, reflect: true })
    accessor opened = false;

    @property({ type: Boolean, reflect: true })
    accessor minimized = false;

    @property({ type: Boolean })
    accessor sticky = false;

    @property({ type: Boolean })
    accessor exitOverlay = false;

    private deepChat?: DeepChat;
    private _closeClickCount = 0;
    private _isConversationDone = false;
    private _shouldEndConversation = false;
    private _broadcastController = new BroadcastController<ChatbotBroadcastMessage>(
        this,
        LR_CHATBOT_BROADCAST_CHANNEL_ID
    );
    private _conversationHistoryTask = this.createConversationHistoryTask();
    private conversationTimeout?: ReturnType<typeof setTimeout>;

    constructor() {
        super({
            translationsLoaderGetter: () => new ChatbotTranslationsLoader(this.apiUrl, this.country, this.language),
            fetchAsyncServerTranslations: true
        });
        this.registerBroadcastMessages();
    }

    getVersion() {
        return 'LR Chatbot - v' + __PKG_VERSION__;
    }

    async connectedCallback() {
        super.connectedCallback();

        this._conversationHistoryTask.run();

        const savedState = localStorage.getItem(LR_CHATBOT_LOCALSTORAGE_CONVERSATION_STATE) as ChatbotDisplayState;
        if (savedState) {
            const isMobile = () => {
                const mediaQueryMobile = `(max-width: ${SizeLrBreakpointMd})`;
                const mediaQueryList = window.matchMedia(mediaQueryMobile);
                return mediaQueryList.matches;
            };
            if (savedState === 'minimized' || (savedState === 'opened' && isMobile())) {
                this.minimize();
            } else if (savedState === 'opened' && !this.isWebviewPage()) {
                this.open();
            }
        }
    }

    private registerBroadcastMessages() {
        this._broadcastController.onMessage = async (message) => {
            if (message.source !== this._broadcastController.sourceId) {
                this._broadcastController.enableSending = false;
                switch (message.action) {
                    case 'open':
                        this.open();
                        break;
                    case 'close':
                        this.close();
                        break;
                    case 'addMessage':
                        this.deepChat?.addMessage(message.payload.message);
                        break;
                    case 'end-conversation':
                        this.endConversation(message.payload.conversationId);
                        break;
                }
                this._broadcastController.enableSending = true;
            }
        };
    }

    private observeInputChanges() {
        const inputElement = this.deepChat?.shadowRoot?.querySelector('#text-input') as HTMLInputElement;
        if (inputElement) {
            inputElement.addEventListener('input', (e) => {
                if (
                    (e as InputEvent).inputType === 'deleteContentBackward' ||
                    (e as InputEvent).inputType === 'insertText'
                ) {
                    this.setConversationTimeout();
                }
            });
        }
    }

    private async handleDeepChatLoad(deepChat?: Element) {
        this.deepChat = deepChat as DeepChat;
        if (this.deepChat) {
            this.setDeepChatStyle();
            this.setDeepChatTranslations();
            this.setChatConnection();
            await this.setDeepChatHistory();
        }
    }

    private getCurrentConversationId() {
        return localStorage.getItem(LR_CHATBOT_LOCALSTORAGE_CONVERSATION_ID);
    }

    private createConversationHistoryTask() {
        return new Task(this, {
            autoRun: false,
            task: async () => await this.tryGetConversationHistory(),
            args: () => []
        });
    }

    private async tryGetConversationHistory() {
        await this.translationsTask?.serverTranslationsLoaded;
        await sleep(200); // for UX

        const conversationId = this.getCurrentConversationId();
        if (conversationId) {
            try {
                const service = new ChatbotService({
                    baseURL: this.apiUrl,
                    withCredentials: true,
                    method: 'GET'
                });
                const historyResponse = await service.chatbotConversationsGet({ id: conversationId });
                return historyResponse;
            } catch {
                console.error('Error while fetching chatbot conversation history');
                localStorage.removeItem(LR_CHATBOT_LOCALSTORAGE_CONVERSATION_ID);
            }
        }
        return;
    }

    private setInitialMessages() {
        if (!this.deepChat) {
            throw Error('Deep Chat should be initialized at this point');
        }
        this.deepChat.history = [
            {
                role: 'lr',
                html: t('chatbot:legalInformation')
            },
            {
                role: 'ai',
                html: createChatbotMessageHtml({ slot: t('chatbot:introMessage') })
            }
        ];
    }

    private async setDeepChatHistory(): Promise<void> {
        if (!this.deepChat) {
            throw Error('Deep Chat should be initialized at this point');
        }

        this.disableDeepChatTextInput();

        if (([TaskStatus.INITIAL, TaskStatus.ERROR] as TaskStatus[]).includes(this._conversationHistoryTask.status)) {
            this._conversationHistoryTask.run();
        }

        const conversationHistory = await this._conversationHistoryTask.taskComplete;

        this.setInitialMessages();

        if (conversationHistory?.messages) {
            this.deepChat.history = [
                ...(this.deepChat.history ?? []),
                ...conversationHistory.messages.map((msg) => {
                    return {
                        role: msg.sender === 'system' ? 'ai' : 'user',
                        html: createChatbotMessageHtml({
                            showEscalation: msg.status === 'human_escalate',
                            apiUrl: this.apiUrl,
                            conversationId: this.getCurrentConversationId() ?? '',
                            messageId: msg.messageId ?? '',
                            score: msg.score ?? undefined,
                            slot: msg.message ?? '',
                            showMessageRating: msg.popUpRate ?? false
                        })
                    };
                })
            ];
        }

        this.enableDeepChatTextInput();

        this._conversationHistoryTask = this.createConversationHistoryTask();
    }

    private setDeepChatTranslations() {
        if (!this.deepChat) {
            throw Error('Deep Chat should be initialized at this point');
        }
        this.deepChat.textInput = {
            ...this.deepChat.textInput,
            placeholder: { text: t('chatbot:inputPlaceholder') }
        };
    }

    private setDeepChatStyle() {
        if (!this.deepChat) {
            throw Error('Deep Chat should be initialized at this point');
        }

        this.deepChat.avatars = DeepChatStyle.avatars;
        this.deepChat.chatStyle = DeepChatStyle.chatStyle;
        this.deepChat.messageStyles = DeepChatStyle.messageStyles;
        this.deepChat.textInput = DeepChatStyle.textInput;
        this.deepChat.submitButtonStyles = DeepChatStyle.submitButtonStyles;
        this.deepChat.inputAreaStyle = DeepChatStyle.inputAreaStyle;
        this.deepChat.htmlClassUtilities = {
            ...DeepChatStyle.htmlClassUtilities,
            suggestion: {
                events: {
                    click: (event) => {
                        if (!this._isConversationDone) {
                            this.deepChat?.submitUserMessage({ text: (event.target as HTMLElement).innerText });
                        }
                    }
                }
            }
        };
        this.deepChat.auxiliaryStyle = DeepChatStyle.auxiliaryStyle;
    }

    private setConversationTimeout() {
        const timeout = Number(t('chatbot:evaluationTimeout'));
        if (this.conversationTimeout) {
            clearTimeout(this.conversationTimeout);
        }
        this.conversationTimeout = setTimeout(() => {
            if (!this._shouldEndConversation) {
                const conversationId = this.getCurrentConversationId();
                this._shouldEndConversation = true;
                this.endConversation(conversationId);
            }
        }, timeout);
    }

    private setChatConnection() {
        if (!this.deepChat) {
            throw Error('Deep Chat should be initialized at this point');
        }
        this.deepChat.connect = {
            handler: async (body, signals) => {
                try {
                    const rawMessage = body.messages[0].text;
                    // Sanitize user input before sending to backend to prevent XSS
                    const message = sanitizeUserInput(rawMessage);

                    if (!message) {
                        // If message is empty after sanitization, show error
                        throw new Error('Invalid message content');
                    }

                    const service = new ChatbotService({
                        baseURL: this.apiUrl,
                        withCredentials: true,
                        method: 'POST'
                    });
                    const postData: SendQuestionRequestModel = {
                        language: this.language.split('-')[0].toLocaleUpperCase(),
                        question: message,
                        source: {
                            platform: isMobileDevice()
                                ? isAppDevice()
                                    ? 'application_webview'
                                    : 'mobile_website'
                                : 'website',
                            channel: 'web'
                        },
                        context: []
                    };
                    if (this.conversationTimeout) {
                        clearTimeout(this.conversationTimeout);
                    }
                    const currentConversationId = this.getCurrentConversationId();
                    if (currentConversationId) {
                        postData['conversation_id'] = currentConversationId;
                    }
                    const questionResponse = await service.chatbotConversationsPost(postData);

                    if (!questionResponse.message) {
                        throw Error();
                    }
                    // Save new conversation id.
                    if (questionResponse.conversation_id && currentConversationId === null) {
                        localStorage.setItem(LR_CHATBOT_LOCALSTORAGE_CONVERSATION_ID, questionResponse.conversation_id);
                    }

                    const messageHtml = createChatbotMessageHtml({
                        apiUrl: this.apiUrl,
                        conversationId: questionResponse.conversation_id ?? undefined,
                        messageId: questionResponse.message_id ?? undefined,
                        showEscalation: questionResponse.popup_escalation,
                        showMessageRating: questionResponse.popup_rate,
                        slot: questionResponse.message
                    });

                    this._shouldEndConversation = questionResponse.message_status === 'end_conversation';
                    this.setConversationTimeout();
                    this.observeInputChanges();
                    signals.onResponse({ html: messageHtml, role: 'ai' });
                } catch {
                    const messageHtml = createChatbotMessageHtml({
                        slot: t('chatbot:errorMessage')
                    });
                    signals.onResponse({
                        html: messageHtml
                    });
                }
            }
        };

        this.deepChat.onMessage = (body) => {
            if (!body.isHistory) {
                this._broadcastController.send({ action: 'addMessage', payload: { message: body.message } });
                if (this._shouldEndConversation) {
                    const conversationId = this.getCurrentConversationId();
                    this.endConversation(conversationId);
                }
                setTimeout(() => {
                    this.deepChat?.scrollToBottom();
                }, 10);
            }
        };
    }

    private endConversation(conversationId: string | null) {
        if (!this._isConversationDone) {
            this._isConversationDone = true;
            this.deepChat?.addMessage({
                role: 'ai',
                html: createChatbotMessageHtml({
                    conversationId: conversationId ?? undefined,
                    apiUrl: this.apiUrl,
                    showConversationRating: true,
                    slot: t('chatbot:ratingConversation')
                })
            });

            if (this.conversationTimeout) {
                clearTimeout(this.conversationTimeout);
                this.conversationTimeout = undefined;
            }

            // Dispatch conversation ending to other tabs only if it has not already be done (can be detected by if conv id is in storage)
            const currentConversationId = this.getCurrentConversationId();
            if (currentConversationId) {
                localStorage.removeItem(LR_CHATBOT_LOCALSTORAGE_CONVERSATION_ID);
                localStorage.removeItem(LR_CHATBOT_LOCALSTORAGE_CONVERSATION_STATE);
                this._broadcastController.send({
                    action: 'end-conversation',
                    payload: { conversationId: currentConversationId }
                });
            }
        }
        this._isConversationDone = false;
        this._shouldEndConversation = false;
    }

    private disableDeepChatTextInput() {
        if (this.deepChat?.inputAreaStyle) {
            this.deepChat.shadowRoot
                ?.querySelector('#input')
                ?.setAttribute('style', styleObjectToString({ ...DeepChatStyle.inputAreaStyle, visibility: 'hidden' }));
        }
        this.deepChat?.disableSubmitButton();
    }

    private enableDeepChatTextInput() {
        if (this.deepChat?.inputAreaStyle) {
            if (this.deepChat?.inputAreaStyle) {
                this.deepChat.shadowRoot
                    ?.querySelector('#input')
                    ?.setAttribute(
                        'style',
                        styleObjectToString({ ...DeepChatStyle.inputAreaStyle, visibility: 'visible' })
                    );
            }
        }
        this.deepChat?.disableSubmitButton(false);
    }

    private handleClosure() {
        this._closeClickCount++;
        if (this._closeClickCount === 1) {
            this.exitOverlay = true;
            this.disableDeepChatTextInput();
        } else if (this._closeClickCount === 2) {
            this.close();
            this._closeClickCount = 0;
        }
    }

    private handleReturnConversation() {
        this.enableDeepChatTextInput();
        this.exitOverlay = false;
        this._closeClickCount = 0;
    }

    private clearConversation() {
        this._isConversationDone = false;
        this._shouldEndConversation = false;
        if (this.deepChat) {
            this.deepChat?.clearMessages();
            this.setInitialMessages();
            this.enableDeepChatTextInput();
        }
    }

    private isWebviewPage() {
        const bodyElement = document.querySelector('body');
        return bodyElement !== null && bodyElement.classList.contains('app-laredoute');
    }

    minimize() {
        if (!this.minimized) {
            this.opened = false;
            this.minimized = true;
            localStorage.setItem(LR_CHATBOT_LOCALSTORAGE_CONVERSATION_STATE, 'minimized' as ChatbotDisplayState);
        }
    }

    open() {
        if (!this.opened) {
            if (this.isWebviewPage()) {
                window.location.href = 'laredoute://?chat';
            } else {
                this.opened = true;
                this.minimized = false;
                localStorage.setItem(LR_CHATBOT_LOCALSTORAGE_CONVERSATION_STATE, 'opened' as ChatbotDisplayState);

                this._broadcastController.send({ action: 'open' });
                const chabotWindowEvent = new CustomEvent('chatBotWindowOpen', { detail: 'true' });
                window.dispatchEvent(chabotWindowEvent);
            }
        }
    }

    close() {
        if (this.opened || this.minimized) {
            localStorage.removeItem(LR_CHATBOT_LOCALSTORAGE_CONVERSATION_ID);
            localStorage.removeItem(LR_CHATBOT_LOCALSTORAGE_CONVERSATION_STATE);
            this.opened = false;
            this.minimized = false;
            this.exitOverlay = false;
            this._closeClickCount = 0;
            this.clearConversation();

            this._broadcastController.send({ action: 'close' });
        }
    }

    renderExitOverlay() {
        return html`
            <div
                ${animate({
                    in: fadeIn,
                    out: fadeOut
                })}
                id="exit-overlay"
                aria-hidden=${!this.exitOverlay}
            >
                <p>${t('chatbot:exit.text')}</p>
                <div>
                    <lr-button @click=${this.handleReturnConversation} variant="secondary" size="small"
                        >${t('chatbot:returnToConversation')}</lr-button
                    >
                    <lr-button size="small" @click=${this.close}>${t('chatbot:exit.button')}</lr-button>
                </div>
            </div>
        `;
    }

    renderChatPopin() {
        return html`
            <div
                id="chat-wrapper"
                aria-hidden="${!this.opened}"
                data-opened="${this.opened}"
                data-sticky="${this.sticky}"
                aria-label="chat window"
                role="region"
                part="wrapper"
                ${animate()}
            >
                <div id="chat-wrapper-menu" part="menu">
                    <span id="chat-wrapper-menu-title">${t('chatbot:title')}</span>
                    <div id="chat-wrapper-menu-button-container">
                        <lr-icon-button
                            size="small"
                            icon="less"
                            class="chat-wrapper-menu-button"
                            id="chat-wrapper-menu-button-minimize"
                            aria-label=""
                            aria-controls="chat-wrapper"
                            aria-expanded=${this.opened}
                            variant="tertiary-transparent"
                            @click=${this.minimize}
                        ></lr-icon-button>
                        <lr-icon-button
                            size="small"
                            icon="close"
                            class="chat-wrapper-menu-button"
                            id="chat-wrapper-menu-button-close"
                            aria-label=""
                            aria-controls="chat-wrapper"
                            @click=${this.handleClosure}
                            variant="tertiary-transparent"
                        ></lr-icon-button>
                    </div>
                </div>
                <div id="chat-wrapper-content">
                    ${this.exitOverlay ? this.renderExitOverlay() : nothing}
                    ${this._conversationHistoryTask.status === TaskStatus.PENDING
                        ? html`<lr-loading-overlay
                              ${animate({
                                  in: fadeIn,
                                  out: fadeOut
                              })}
                          ></lr-loading-overlay>`
                        : nothing}
                    <deep-chat id="chat-element" part="chat-element" ${ref(this.handleDeepChatLoad)}></deep-chat>
                </div>
            </div>
        `;
    }
    renderMinimizedChat() {
        return html`
            <lr-chatbot-minimized id="chat-minimizerd-wrapper" .open=${() => this.open()}></lr-chatbot-minimized>
        `;
    }

    renderElement() {
        return html`${this.opened ? this.renderChatPopin() : this.minimized ? this.renderMinimizedChat() : nothing}`;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'lr-chatbot': Chatbot;
    }
}
